import React, { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Layers, Wand2, Eye, AlertCircle } from 'lucide-react';
import { FormField, FIELD_TYPES, PromptBuilderConfig } from '../../../types/formBuilder';
import { createNewField } from '../../../utils/formBuilderHelpers';

interface PromptBuilderSettingsProps {
  field: FormField;
  onUpdate: (updates: Partial<FormField>) => void;
}

export const PromptBuilderSettings: React.FC<PromptBuilderSettingsProps> = ({ field, onUpdate }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewValues, setPreviewValues] = useState<Record<string, any>>({});

  const config = field.promptBuilderConfig || {
    template: '',
    sub_fields: []
  };

  const updateConfig = (updates: Partial<PromptBuilderConfig>) => {
    onUpdate({
      promptBuilderConfig: {
        ...config,
        ...updates
      }
    });
  };

  const addSubField = () => {
    const newField = createNewField();
    updateConfig({
      sub_fields: [...config.sub_fields, newField]
    });
  };

  const updateSubField = (index: number, updates: Partial<FormField>) => {
    const newSubFields = [...config.sub_fields];
    newSubFields[index] = { ...newSubFields[index], ...updates };
    updateConfig({ sub_fields: newSubFields });
  };

  const removeSubField = (index: number) => {
    const newSubFields = [...config.sub_fields];
    newSubFields.splice(index, 1);
    updateConfig({ sub_fields: newSubFields });
  };

  const moveSubFieldUp = (index: number) => {
    if (index === 0) return;
    const newSubFields = [...config.sub_fields];
    [newSubFields[index - 1], newSubFields[index]] = [newSubFields[index], newSubFields[index - 1]];
    updateConfig({ sub_fields: newSubFields });
  };

  const moveSubFieldDown = (index: number) => {
    if (index === config.sub_fields.length - 1) return;
    const newSubFields = [...config.sub_fields];
    [newSubFields[index], newSubFields[index + 1]] = [newSubFields[index + 1], newSubFields[index]];
    updateConfig({ sub_fields: newSubFields });
  };

  // Extract placeholders from template
  const extractPlaceholders = (template: string): string[] => {
    const matches = template.match(/\{([^}]+)\}/g);
    return matches ? matches.map(match => match.slice(1, -1)) : [];
  };

  const placeholders = extractPlaceholders(config.template);

  // Build preview prompt
  const buildPreviewPrompt = (): string => {
    let prompt = config.template;
    
    config.sub_fields.forEach(subField => {
      const placeholder = `{${subField.variable_name}}`;
      const value = previewValues[subField.variable_name] || subField.defaultValue || `[${subField.label}]`;
      prompt = prompt.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), String(value));
    });
    
    return prompt;
  };

  const renderSubFieldPreview = (subField: FormField, index: number) => {
    const fieldType = FIELD_TYPES.find(type => type.value === subField.type);
    
    return (
      <div key={subField.id} className="bg-elevated border border-subtle rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {fieldType?.icon && (
              <fieldType.icon className="w-4 h-4 text-brand" />
            )}
            <span className="text-sm font-medium text-th-primary">
              {subField.label || 'Unnamed Field'}
            </span>
            <span className="text-xs text-accent bg-accent/20 px-2 py-0.5 rounded">
              {fieldType?.label || subField.type}
            </span>
            {subField.required && (
              <span className="text-xs text-status-error font-medium">*</span>
            )}
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={() => moveSubFieldUp(index)}
              disabled={index === 0}
              className="p-1 text-th-secondary hover:text-brand transition-colors rounded disabled:opacity-30 disabled:cursor-not-allowed"
              title="Move up"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <button
              onClick={() => moveSubFieldDown(index)}
              disabled={index === config.sub_fields.length - 1}
              className="p-1 text-th-secondary hover:text-brand transition-colors rounded disabled:opacity-30 disabled:cursor-not-allowed"
              title="Move down"
            >
              <ChevronDown className="w-3 h-3" />
            </button>
            <button
              onClick={() => removeSubField(index)}
              className="p-1 text-th-secondary hover:text-status-error transition-colors rounded hover:bg-status-error/10"
              title="Remove field"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Label */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-th-secondary">
              Label
            </label>
            <input
              type="text"
              value={subField.label}
              onChange={(e) => updateSubField(index, { label: e.target.value })}
              placeholder="Field label"
              className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm"
            />
          </div>

          {/* Variable Name */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-th-secondary">
              Variable Name
            </label>
            <input
              type="text"
              value={subField.variable_name}
              onChange={(e) => updateSubField(index, { variable_name: e.target.value })}
              placeholder="variable_name"
              className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm font-mono"
            />
          </div>

          {/* Type */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-th-secondary">
              Type
            </label>
            <select
              value={subField.type}
              onChange={(e) => updateSubField(index, { type: e.target.value as any })}
              className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary focus:border-brand focus:outline-none text-sm"
            >
              <option value="">Select Type</option>
              {FIELD_TYPES.filter(type => !['prompt_builder', 'file'].includes(type.value)).map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Default Value */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-th-secondary">
              Default Value
            </label>
            <input
              type="text"
              value={subField.defaultValue || ''}
              onChange={(e) => updateSubField(index, { defaultValue: e.target.value })}
              placeholder="Default value"
              className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm"
            />
          </div>
        </div>

        {/* Select Options for sub-field */}
        {subField.type === 'select' && (
          <div className="mt-3 space-y-2">
            <label className="block text-xs font-medium text-th-secondary">
              Options
            </label>
            <div className="space-y-2">
              {(subField.options || []).map((option, optionIndex) => (
                <div key={optionIndex} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={option.label}
                    onChange={(e) => {
                      const newOptions = [...(subField.options || [])];
                      newOptions[optionIndex] = { ...newOptions[optionIndex], label: e.target.value };
                      updateSubField(index, { options: newOptions });
                    }}
                    placeholder="Label"
                    className="flex-1 bg-surface border border-subtle rounded px-2 py-1 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-xs"
                  />
                  <input
                    type="text"
                    value={option.value}
                    onChange={(e) => {
                      const newOptions = [...(subField.options || [])];
                      newOptions[optionIndex] = { ...newOptions[optionIndex], value: e.target.value };
                      updateSubField(index, { options: newOptions });
                    }}
                    placeholder="value"
                    className="flex-1 bg-surface border border-subtle rounded px-2 py-1 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-xs font-mono"
                  />
                  <button
                    onClick={() => {
                      const newOptions = [...(subField.options || [])];
                      newOptions.splice(optionIndex, 1);
                      updateSubField(index, { options: newOptions });
                    }}
                    className="p-1 text-th-secondary hover:text-status-error transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const newOptions = [...(subField.options || []), { label: '', value: '' }];
                  updateSubField(index, { options: newOptions });
                }}
                className="flex items-center space-x-1 px-2 py-1 bg-brand/20 text-brand rounded text-xs font-medium hover:bg-brand/30 transition-all"
              >
                <Plus className="w-2 h-2" />
                <span>Add Option</span>
              </button>
            </div>
          </div>
        )}

        {/* Required Toggle */}
        <div className="mt-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={subField.required}
              onChange={(e) => updateSubField(index, { required: e.target.checked })}
              className="w-3 h-3 text-brand bg-surface border-subtle rounded focus:ring-brand focus:ring-1"
            />
            <span className="text-th-primary text-sm">Required field</span>
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3 p-4 bg-brand/10 border border-brand/20 rounded-lg">
        <Layers className="w-6 h-6 text-brand" />
        <div>
          <h3 className="text-lg font-semibold text-th-primary">Prompt Builder Configuration</h3>
          <p className="text-sm text-th-secondary">
            Create guided prompt building with multiple input fields and templates
          </p>
        </div>
      </div>

      {/* Template Editor */}
      <div className="space-y-4 p-4 bg-status-warning/5 border border-status-warning/20 rounded-lg">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-semibold text-th-primary flex items-center space-x-2">
            <Wand2 className="w-4 h-4 text-status-warning" />
            <span>Prompt Template</span>
          </h4>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center space-x-1 px-3 py-1.5 bg-accent text-surface rounded-lg text-sm font-medium hover:bg-accent/90 transition-all"
          >
            <Eye className="w-3 h-3" />
            <span>{showPreview ? 'Hide Preview' : 'Show Preview'}</span>
          </button>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-th-primary">
            Template String *
          </label>
          <textarea
            value={config.template}
            onChange={(e) => updateConfig({ template: e.target.value })}
            placeholder="Generate a {subject} in {style} style with {lighting} lighting. The {subject} should be {mood} and {detail_level}."
            rows={4}
            className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none resize-none transition-all"
          />
          <div className="flex items-start space-x-4">
            <div className="flex-1">
              <p className="text-xs text-th-secondary">
                Use <code className="text-status-warning font-mono bg-status-warning/20 px-1 rounded">{`{variable_name}`}</code> placeholders for sub-field values
              </p>
            </div>
            {placeholders.length > 0 && (
              <div className="flex-1">
                <p className="text-xs text-status-warning font-medium mb-1">Found placeholders:</p>
                <div className="flex flex-wrap gap-1">
                  {placeholders.map((placeholder, index) => (
                    <span key={index} className="text-xs px-2 py-0.5 bg-status-warning/20 text-status-warning rounded font-mono">
                      {placeholder}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Template Preview */}
        {showPreview && config.template && (
          <div className="space-y-3 p-4 bg-surface border border-subtle rounded-lg">
            <h5 className="text-sm font-medium text-th-primary">Live Template Preview</h5>
            
            {/* Mock sub-field inputs for preview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {placeholders.map((placeholder) => {
                const subField = config.sub_fields.find(f => f.variable_name === placeholder);
                return (
                  <div key={placeholder} className="space-y-1">
                    <label className="block text-xs font-medium text-th-secondary">
                      {subField?.label || placeholder}
                    </label>
                    <input
                      type="text"
                      value={previewValues[placeholder] || ''}
                      onChange={(e) => setPreviewValues(prev => ({ 
                        ...prev, 
                        [placeholder]: e.target.value 
                      }))}
                      placeholder={`Enter ${placeholder}`}
                      className="w-full bg-elevated border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm"
                    />
                  </div>
                );
              })}
            </div>
            
            {/* Generated Prompt Preview */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-th-primary">
                Generated Prompt:
              </label>
              <div className="p-3 bg-status-success/10 border border-status-success/30 rounded-lg">
                <p className="text-status-success text-sm font-medium">
                  {buildPreviewPrompt()}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sub-Fields Management */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-semibold text-th-primary flex items-center space-x-2">
            <span>Sub-Fields ({config.sub_fields.length})</span>
          </h4>
          <button
            onClick={addSubField}
            className="flex items-center space-x-1 px-3 py-1.5 bg-brand text-surface rounded-lg text-sm font-medium hover:bg-brand/90 transition-all"
          >
            <Plus className="w-3 h-3" />
            <span>Add Sub-Field</span>
          </button>
        </div>

        {/* Validation Warning */}
        {placeholders.length > 0 && config.sub_fields.length === 0 && (
          <div className="p-3 bg-status-warning/10 border border-status-warning/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-status-warning" />
              <span className="text-status-warning font-medium text-sm">Template Needs Sub-Fields</span>
            </div>
            <p className="text-status-warning/70 text-xs mt-1">
              Your template has placeholders but no sub-fields. Add sub-fields with matching variable names.
            </p>
          </div>
        )}

        {/* Sub-Fields List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {config.sub_fields.length === 0 ? (
            <div className="text-center py-8 text-th-secondary">
              <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No sub-fields added yet</p>
              <p className="text-xs">Click "Add Sub-Field" to create guided input fields</p>
            </div>
          ) : (
            config.sub_fields.map((subField, index) => renderSubFieldPreview(subField, index))
          )}
        </div>

        {/* Configuration Tips */}
        <div className="bg-status-warning/5 border border-status-warning/20 rounded-lg p-4">
          <h5 className="text-status-warning font-medium text-sm mb-2">💡 Configuration Tips</h5>
          <ul className="text-th-secondary text-xs space-y-1 list-disc list-inside">
            <li>Template placeholders must match sub-field variable names exactly</li>
            <li>Use descriptive placeholders like <code className="text-status-warning font-mono">{`{subject}`}</code>, <code className="text-status-warning font-mono">{`{style}`}</code>, <code className="text-status-warning font-mono">{`{lighting}`}</code></li>
            <li>Sub-fields can be text, select, boolean, number, or range types</li>
            <li>Required sub-fields will be validated before prompt generation</li>
            <li>Default values help pre-fill the template for better UX</li>
          </ul>
        </div>
      </div>
    </div>
  );
};